import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { boardwalkPlanks, sandTexture, skyGradient } from "./textures";

// What you see out of the Burger Shack's pickup window: a concrete pad in
// front of the shack, the boardwalk running past with its railing, benches
// and lamp posts, the beach and the sea out to the horizon, and in the
// distance a roller coaster (a train going round) and a Ferris wheel
// (slowly turning) under a few clouds. All low-poly: repeated parts are
// instanced, distant parts are flat-shaded, and fog melts the far edge into
// the sky.
//
// The shack's front wall is at z = `front`, and the outside is +z from it.

/** How far the camera needs to see to take it all in. */
export const VIEW_FAR = 420;
export const SKY = 0xcfe9f7;

/** Ground levels: the boardwalk is flush with the shack's floor; the beach is lower. */
const SAND_Y = -0.9;
const SEA_Y = -1.0;

export interface Outside {
  group: THREE.Group;
  fog: THREE.Fog;
  /** Turn the wheel and run the coaster's train. */
  animate(time: number): void;
}

/**
 * Outdoor paint. The kitchen's key light is indoors, so outside things carry
 * their own daylight: part of their colour glows, the rest takes the shading.
 */
const DAYLIGHT = 0.55;
export const flat = (color: number) => new THREE.MeshLambertMaterial({ color, emissive: color, emissiveIntensity: DAYLIGHT });
export const textured = (map: THREE.Texture, daylight = DAYLIGHT) => new THREE.MeshLambertMaterial({ map, emissive: 0xffffff, emissiveMap: map, emissiveIntensity: daylight });

export function buildBoardwalk(front: number): Outside {
  const g = new THREE.Group();
  const z0 = front;

  // The sky: a gradient dome that ignores the fog.
  const sky = new THREE.Mesh(new THREE.SphereGeometry(VIEW_FAR - 20, 24, 12), new THREE.MeshBasicMaterial({ map: skyGradient(), side: THREE.BackSide, fog: false }));
  sky.position.set(3, 0, z0);
  g.add(sky);

  // Clouds: puffs squashed flat, far off.
  const puff = new THREE.SphereGeometry(1, 10, 6);
  const cloudMat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false });
  for (const [cx, cy, cz, s] of [
    [-60, 55, 230, 14],
    [40, 70, 280, 18],
    [120, 48, 200, 11],
    [-150, 62, 260, 16],
  ] as const) {
    for (let k = 0; k < 4; k++) {
      const m = new THREE.Mesh(puff, cloudMat);
      m.position.set(cx + (k - 1.5) * s * 0.9, cy + (k % 2) * s * 0.25, cz + k * 3);
      m.scale.set(s, s * 0.45, s * 0.6);
      g.add(m);
    }
  }

  // The concrete pad in front of the shack, where the line forms.
  const pad = new THREE.Mesh(new THREE.PlaneGeometry(40, 1.8), flat(0xb9b5ab));
  pad.rotation.x = -Math.PI / 2;
  pad.position.set(3, 0, z0 + 0.9);
  g.add(pad);

  // The boardwalk: planks across it, running off both ways.
  const bw0 = z0 + 1.8;
  const bw1 = bw0 + 4.2;
  const walkLength = 160;
  const walk = new THREE.Mesh(new THREE.PlaneGeometry(walkLength, bw1 - bw0), textured(boardwalkPlanks(walkLength)));
  walk.rotation.x = -Math.PI / 2;
  walk.position.set(3, 0.01, (bw0 + bw1) / 2);
  g.add(walk);
  // Its edge, down to the sand.
  const skirt = new THREE.Mesh(new THREE.BoxGeometry(walkLength, -SAND_Y, 0.2), flat(0x6e4a2a));
  skirt.position.set(3, SAND_Y / 2, bw1 + 0.1);
  g.add(skirt);

  // The railing along the sea side: posts every two metres, two rails.
  const railZ = bw1 - 0.08;
  const wood = flat(0x8a5a33);
  const postCount = Math.floor(walkLength / 2);
  const posts = new THREE.InstancedMesh(new THREE.BoxGeometry(0.1, 1.05, 0.1), wood, postCount);
  const m4 = new THREE.Matrix4();
  for (let i = 0; i < postCount; i++) posts.setMatrixAt(i, m4.makeTranslation(3 - walkLength / 2 + i * 2, 0.52, railZ));
  g.add(posts);
  for (const y of [1.02, 0.6]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(walkLength, 0.07, 0.08), wood);
    rail.position.set(3, y, railZ);
    g.add(rail);
  }

  // Benches facing the sea, and lamp posts.
  const benchWood = flat(0x9c6b3e);
  const iron = flat(0x2f3b3a);
  for (const bx of [-1.5, 3.9, 9.5, 16]) {
    const bench = new THREE.Group();
    const seat = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.06, 0.45), benchWood);
    seat.position.y = 0.45;
    const backrest = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.4, 0.05), benchWood);
    backrest.position.set(0, 0.75, -0.22);
    backrest.rotation.x = -0.15;
    bench.add(seat, backrest);
    for (const dx of [-0.8, 0.8]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.45, 0.4), iron);
      leg.position.set(dx, 0.22, 0);
      bench.add(leg);
    }
    bench.position.set(bx, 0, railZ - 0.6);
    g.add(bench);
  }
  const globe = new THREE.MeshBasicMaterial({ color: 0xfff3c4 });
  for (const lx of [-5, 6.8, 19]) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 3.4, 8), iron);
    pole.position.set(lx, 1.7, railZ - 0.15);
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.17, 12, 8), globe);
    lamp.position.set(lx, 3.5, railZ - 0.15);
    g.add(pole, lamp);
  }

  // The beach, the surf line and the sea.
  const beachDepth = 40;
  const sand = new THREE.Mesh(new THREE.PlaneGeometry(500, beachDepth), textured(sandTexture(), 0.3));
  sand.rotation.x = -Math.PI / 2;
  sand.position.set(3, SAND_Y, bw1 + beachDepth / 2);
  g.add(sand);
  const shore = bw1 + beachDepth;
  const foam = new THREE.Mesh(new THREE.PlaneGeometry(500, 1.6), flat(0xf4f7f2));
  foam.rotation.x = -Math.PI / 2;
  foam.position.set(3, SAND_Y + 0.02, shore);
  g.add(foam);
  const sea = new THREE.Mesh(new THREE.PlaneGeometry(1200, VIEW_FAR), flat(0x2f86b4));
  sea.rotation.x = -Math.PI / 2;
  sea.position.set(3, SEA_Y, shore + VIEW_FAR / 2);
  g.add(sea);

  // A few palms on the sand.
  const trunkMat = flat(0x8b6b4a);
  const frondMat = flat(0x3f8f3a);
  for (const [px, pz, h] of [
    [-7, bw1 + 6, 6],
    [12.5, bw1 + 9, 7],
    [25, bw1 + 5, 5.5],
  ] as const) {
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.25, h, 7), trunkMat);
    trunk.position.set(px, SAND_Y + h / 2, pz);
    trunk.rotation.z = 0.08;
    g.add(trunk);
    for (let k = 0; k < 6; k++) {
      const frond = new THREE.Mesh(new THREE.ConeGeometry(0.45, 2.6, 4), frondMat);
      const a = (k / 6) * Math.PI * 2;
      frond.position.set(px + Math.cos(a) * 1.1 - h * 0.08, SAND_Y + h - 0.2, pz + Math.sin(a) * 1.1);
      frond.rotation.set(Math.sin(a) * 1.9, 0, -Math.cos(a) * 1.9);
      frond.scale.z = 0.3;
      g.add(frond);
    }
  }

  // The Ferris wheel, off to the right on the beach.
  const wheel = new THREE.Group();
  const hub = new THREE.Vector3(48, 27, bw1 + 85);
  const R = 23;
  const steelWhite = flat(0xf2f2ee);
  wheel.add(new THREE.Mesh(new THREE.TorusGeometry(R, 0.35, 6, 48), steelWhite));
  wheel.add(new THREE.Mesh(new THREE.TorusGeometry(R * 0.55, 0.2, 6, 36), steelWhite));
  const seats = 14;
  for (let i = 0; i < seats; i++) {
    const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.15, R, 0.15), steelWhite);
    const a = (i / seats) * Math.PI * 2;
    spoke.position.set((Math.cos(a) * R) / 2, (Math.sin(a) * R) / 2, 0);
    spoke.rotation.z = a - Math.PI / 2;
    wheel.add(spoke);
  }
  wheel.position.copy(hub);
  g.add(wheel);
  // The cars hang level as it turns, so they're placed every frame.
  const carColors = [0xe4473b, 0xf4b942, 0x3b8fd9, 0x45b06a];
  const cars = new THREE.InstancedMesh(new THREE.BoxGeometry(2.2, 2, 2.2), new THREE.MeshLambertMaterial({ emissive: 0x555555 }), seats);
  for (let i = 0; i < seats; i++) cars.setColorAt(i, new THREE.Color(carColors[i % carColors.length]!));
  g.add(cars);
  // Its A-frame legs.
  for (const dz of [-1.6, 1.6]) {
    for (const dx of [-12, 12]) {
      const foot = new THREE.Vector3(hub.x + dx, SAND_Y, hub.z + dz);
      const leg = beam(foot, new THREE.Vector3(hub.x, hub.y, hub.z + dz * 0.6), 0.3, steelWhite);
      g.add(leg);
    }
  }

  // The roller coaster, off to the left: a loop of track with a lift hill and
  // drops, on posts, and a train running round it.
  const track = new THREE.CatmullRomCurve3(
    [
      [-30, 2, 40],
      [-42, 24, 44],
      [-52, 26, 50],
      [-58, 4, 58],
      [-66, 14, 70],
      [-58, 18, 82],
      [-44, 5, 86],
      [-30, 12, 80],
      [-22, 8, 66],
      [-20, 3, 52],
    ].map(([x, y, z]) => new THREE.Vector3(x! * 1.4 - 8, y! * 1.4, z! * 1.4 + bw1 + 20)),
    true,
    "catmullrom",
    0.4,
  );
  const trackMat = flat(0xd8392b);
  g.add(new THREE.Mesh(new THREE.TubeGeometry(track, 260, 0.5, 6, true), trackMat));
  const supportCount = 60;
  const supports = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.18, 0.18, 1, 5), flat(0xeeeeee), supportCount);
  const p = new THREE.Vector3();
  for (let i = 0; i < supportCount; i++) {
    track.getPointAt(i / supportCount, p);
    const h = p.y - SAND_Y;
    supports.setMatrixAt(i, new THREE.Matrix4().compose(new THREE.Vector3(p.x, SAND_Y + h / 2, p.z), new THREE.Quaternion(), new THREE.Vector3(1, h, 1)));
  }
  g.add(supports);
  const trainCars = 5;
  const train = new THREE.InstancedMesh(new THREE.BoxGeometry(2.2, 1.5, 3.2), flat(0xf4b942), trainCars);
  g.add(train);

  const fog = new THREE.Fog(SKY, 60, VIEW_FAR - 40);
  const q = new THREE.Quaternion();
  const one = new THREE.Vector3(1, 1, 1);
  const at = new THREE.Vector3();
  const ahead = new THREE.Vector3();
  const look = new THREE.Matrix4();
  const up = new THREE.Vector3(0, 1, 0);

  function animate(time: number): void {
    // One turn every ninety seconds.
    const turn = (time / 90) * Math.PI * 2;
    wheel.rotation.z = turn;
    for (let i = 0; i < seats; i++) {
      const a = (i / seats) * Math.PI * 2 + turn;
      cars.setMatrixAt(i, m4.makeTranslation(hub.x + Math.cos(a) * R, hub.y + Math.sin(a) * R - 1.5, hub.z));
    }
    cars.instanceMatrix.needsUpdate = true;
    // The train: slow up the lift hill, fast down the drops.
    const lap = (time / 28) % 1;
    for (let i = 0; i < trainCars; i++) {
      const u = (lap - i * 0.009 + 1) % 1;
      track.getPointAt(u, at);
      track.getPointAt((u + 0.002) % 1, ahead);
      look.lookAt(ahead, at, up);
      q.setFromRotationMatrix(look);
      train.setMatrixAt(i, m4.compose(at.setY(at.y + 1.1), q, one));
    }
    train.instanceMatrix.needsUpdate = true;
  }
  animate(0);
  mergeStatic(g);

  return { group: g, fog, animate };
}

/**
 * The scenery's still parts, merged into one mesh per material: a few draw
 * calls instead of a hundred. Instanced and moving parts are left alone.
 */
export function mergeStatic(g: THREE.Group): void {
  const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>();
  for (const child of [...g.children]) {
    if (!(child instanceof THREE.Mesh) || child instanceof THREE.InstancedMesh || Array.isArray(child.material)) continue;
    child.updateMatrix();
    const geo = (child.geometry as THREE.BufferGeometry).clone().applyMatrix4(child.matrix);
    const list = byMaterial.get(child.material) ?? [];
    list.push(geo.index ? geo.toNonIndexed() : geo);
    byMaterial.set(child.material, list);
    g.remove(child);
  }
  for (const [mat, geos] of byMaterial) {
    const merged = mergeGeometries(geos);
    if (merged) g.add(new THREE.Mesh(merged, mat));
  }
}

/** A beam of square section from one point to another. */
function beam(a: THREE.Vector3, b: THREE.Vector3, thick: number, mat: THREE.Material): THREE.Mesh {
  const len = a.distanceTo(b);
  const m = new THREE.Mesh(new THREE.BoxGeometry(thick, len, thick), mat);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  return m;
}
